import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Button, Skeleton, Typography } from '@mui/material'

export interface RoomVariantRowProps {
    variant: 'mobile' | 'desktop'
    mealLabel: string
    inclusions: string[]
    cancellationLabel: string
    cancellationOpen: boolean
    cancellationLoading: boolean
    cancellationText: string
    onToggleCancellation: () => void
    dealBadge?: string
    wasPriceText?: string
    nowPriceText: string
    perStayText: string
    selectLabel: string
    onSelect: () => void
    /** Desktop only: hairline divider below unless this is the last option in its group. */
    isLast: boolean
}

// A single rate/meal option within a room type. Mobile = single-column stack (meal →
// inclusions → cancellation → price → full-width Select) matching the RN redesign;
// desktop keeps a two-column row for horizontal density.
const RoomVariantRow: React.FC<RoomVariantRowProps> = ({
    variant,
    mealLabel,
    inclusions,
    cancellationLabel,
    cancellationOpen,
    cancellationLoading,
    cancellationText,
    onToggleCancellation,
    dealBadge,
    wasPriceText,
    nowPriceText,
    perStayText,
    selectLabel,
    onSelect,
    isLast
}) => {
    const mealInfo = (
        <>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'textColors.primary' }}>{mealLabel}</Typography>

            {inclusions.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                    {inclusions.map(inclusion => (
                        <Box key={inclusion} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 13, color: 'primary.main' }} />
                            <Typography sx={{ fontSize: 11, color: 'textColors.secondary' }}>{inclusion}</Typography>
                        </Box>
                    ))}
                </Box>
            )}

            <Box
                component="button"
                type="button"
                onClick={onToggleCancellation}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 1,
                    p: 0,
                    border: 0,
                    bgcolor: 'transparent',
                    cursor: 'pointer'
                }}
            >
                <InfoOutlinedIcon sx={{ fontSize: 13, color: 'textColors.secondary' }} />
                <Typography sx={{ fontSize: 11, color: 'textColors.secondary' }}>{cancellationLabel}</Typography>
            </Box>
            {cancellationOpen && (
                <Box sx={{ mt: 0.75, p: 1, bgcolor: 'neutral.100', borderRadius: 1 }}>
                    {cancellationLoading ? (
                        <>
                            <Skeleton variant="text" width="90%" height={14} />
                            <Skeleton variant="text" width="65%" height={14} />
                        </>
                    ) : (
                        <Typography sx={{ fontSize: 11, color: 'textColors.primary' }}>{cancellationText}</Typography>
                    )}
                </Box>
            )}
        </>
    )

    const dealBadgeChip = dealBadge ? (
        <Box sx={{ bgcolor: 'success.light', borderRadius: 0.75, px: 0.75, py: 0.25 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'success.main' }}>{dealBadge}</Typography>
        </Box>
    ) : null

    if (variant === 'mobile') {
        return (
            <Box sx={{ p: 1.75 }}>
                {mealInfo}

                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'textColors.primary' }}>
                        {nowPriceText}
                    </Typography>
                    {wasPriceText && (
                        <Typography sx={{ fontSize: 12, color: 'textColors.secondary', textDecoration: 'line-through' }}>
                            {wasPriceText}
                        </Typography>
                    )}
                    {dealBadgeChip}
                </Box>
                <Typography sx={{ fontSize: 11, color: 'textColors.secondary', mt: 0.25 }}>{perStayText}</Typography>

                <Button
                    fullWidth
                    variant="customVariantPrimary"
                    onClick={onSelect}
                    sx={{ mt: 1.5, py: 1, fontSize: 14, fontWeight: 700 }}
                >
                    {selectLabel}
                </Button>
            </Box>
        )
    }

    return (
        <Box sx={{ p: 1.75, borderBottom: isLast ? 0 : 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 180 }}>{mealInfo}</Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 120 }}>
                    {dealBadgeChip && <Box sx={{ mb: 0.5 }}>{dealBadgeChip}</Box>}
                    {wasPriceText && (
                        <Typography sx={{ fontSize: 11, color: 'textColors.secondary', textDecoration: 'line-through' }}>
                            {wasPriceText}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'textColors.primary' }}>
                        {nowPriceText}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'textColors.secondary' }}>{perStayText}</Typography>
                    <Button
                        variant="customVariantPrimary"
                        onClick={onSelect}
                        sx={{ mt: 1, px: 2, py: 0.75, fontSize: 13, fontWeight: 700 }}
                    >
                        {selectLabel}
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}

export { RoomVariantRow }
export default RoomVariantRow
